import { useState, useEffect } from "react";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";

export type EvidenceSummaryMode = "none" | "separate_column" | "concat";

export interface AttributeTableFieldFormData {
  sheetName: string;
  startRow: number;
  attributeNameColumn: string;
  attributeOutcomeColumn: string;
  attributeOutcomeDescriptionColumn: string | null;
  descriptionInstructions: string | null;
  // Evidence summary configuration (mutually exclusive)
  evidenceSummaryColumn: string | null;
  concatEvidenceWithDescription: boolean;
}

interface AttributeTableFieldFormProps {
  value: AttributeTableFieldFormData;
  onChange: (value: AttributeTableFieldFormData) => void;
  readOnly?: boolean;
}

// Derive evidence summary mode from form data
function getEvidenceSummaryMode(
  data: AttributeTableFieldFormData,
): EvidenceSummaryMode {
  if (data.concatEvidenceWithDescription) {
    return "concat";
  }
  if (data.evidenceSummaryColumn) {
    return "separate_column";
  }
  return "none";
}

export function AttributeTableFieldForm({
  value,
  onChange,
  readOnly = false,
}: AttributeTableFieldFormProps) {
  const [formData, setFormData] = useState<AttributeTableFieldFormData>(value);

  useEffect(() => {
    setFormData(value);
  }, [value]);

  const handleChange = (
    field: keyof AttributeTableFieldFormData,
    newValue: string | number | boolean | null,
  ) => {
    const updated = { ...formData, [field]: newValue };
    setFormData(updated);
    onChange(updated);
  };

  const handleEvidenceSummaryModeChange = (mode: EvidenceSummaryMode) => {
    let updated: AttributeTableFieldFormData;
    switch (mode) {
      case "none":
        updated = {
          ...formData,
          evidenceSummaryColumn: null,
          concatEvidenceWithDescription: false,
        };
        break;
      case "separate_column":
        updated = {
          ...formData,
          evidenceSummaryColumn: formData.evidenceSummaryColumn ?? "D",
          concatEvidenceWithDescription: false,
        };
        break;
      case "concat":
        updated = {
          ...formData,
          evidenceSummaryColumn: null,
          concatEvidenceWithDescription: true,
        };
        break;
    }
    setFormData(updated);
    onChange(updated);
  };

  const evidenceSummaryMode = getEvidenceSummaryMode(formData);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="sheetName">Sheet Name</Label>
        <Input
          id="sheetName"
          value={formData.sheetName}
          onChange={(e) => handleChange("sheetName", e.target.value)}
          placeholder="Sheet1"
          disabled={readOnly}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="startRow">Starting Row</Label>
        <Input
          id="startRow"
          type="number"
          min={1}
          value={formData.startRow}
          onChange={(e) =>
            handleChange("startRow", parseInt(e.target.value, 10) || 1)
          }
          placeholder="1"
          disabled={readOnly}
        />
        <p className="text-sm text-muted-foreground">
          The table will expand downward from this row, creating one row per
          attribute
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="attributeNameColumn">Attribute Name Column</Label>
        <Input
          id="attributeNameColumn"
          value={formData.attributeNameColumn}
          onChange={(e) =>
            handleChange("attributeNameColumn", e.target.value.toUpperCase())
          }
          placeholder="A"
          disabled={readOnly}
          className="uppercase"
        />
        <p className="text-sm text-muted-foreground">
          Column for the attribute name (e.g., "User Authorization", "Access
          Controls")
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="attributeOutcomeColumn">Outcome Column</Label>
        <Input
          id="attributeOutcomeColumn"
          value={formData.attributeOutcomeColumn}
          onChange={(e) =>
            handleChange("attributeOutcomeColumn", e.target.value.toUpperCase())
          }
          placeholder="B"
          disabled={readOnly}
          className="uppercase"
        />
        <p className="text-sm text-muted-foreground">
          Column for the pass/fail outcome
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="attributeOutcomeDescriptionColumn">
          Description Column (Optional)
        </Label>
        <Input
          id="attributeOutcomeDescriptionColumn"
          value={formData.attributeOutcomeDescriptionColumn || ""}
          onChange={(e) =>
            handleChange(
              "attributeOutcomeDescriptionColumn",
              e.target.value.toUpperCase() || null,
            )
          }
          placeholder="e.g. C"
          disabled={readOnly}
          className="placeholder:italic"
        />
        <p className="text-sm text-muted-foreground">
          Optional column for comments or outcome descriptions
        </p>
      </div>

      {formData.attributeOutcomeDescriptionColumn && (
        <div className="space-y-2">
          <Label htmlFor="descriptionInstructions">
            Description Instructions (Optional)
          </Label>
          <Textarea
            id="descriptionInstructions"
            value={formData.descriptionInstructions || ""}
            onChange={(e) =>
              handleChange("descriptionInstructions", e.target.value || null)
            }
            placeholder="Example: Focus on quantitative metrics. Use formal audit language. Reference specific control IDs when applicable."
            rows={4}
            disabled={readOnly}
          />
          <p className="text-sm text-muted-foreground">
            Optional guidance for AI-generated outcome descriptions. These
            instructions help customize the language and focus of generated
            descriptions.
          </p>
        </div>
      )}

      <div className="space-y-3 border-t pt-4">
        <Label>Evidence Summary</Label>
        <p className="text-sm text-muted-foreground">
          Configure how evidence summaries are generated and output to the work
          paper. Evidence summaries provide an executive summary of the evidence
          with inline citations.
        </p>
        <RadioGroup
          value={evidenceSummaryMode}
          onValueChange={(v) =>
            handleEvidenceSummaryModeChange(v as EvidenceSummaryMode)
          }
          disabled={readOnly}
          className="space-y-2"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="none" id="evidence-none" />
            <Label
              htmlFor="evidence-none"
              className="font-normal cursor-pointer"
            >
              No evidence summary
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="separate_column" id="evidence-separate" />
            <Label
              htmlFor="evidence-separate"
              className="font-normal cursor-pointer"
            >
              Output to separate column
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="concat" id="evidence-concat" />
            <Label
              htmlFor="evidence-concat"
              className="font-normal cursor-pointer"
            >
              Append to description column
            </Label>
          </div>
        </RadioGroup>

        {evidenceSummaryMode === "separate_column" && (
          <div className="ml-6 space-y-2">
            <Label htmlFor="evidenceSummaryColumn">
              Evidence Summary Column
            </Label>
            <Input
              id="evidenceSummaryColumn"
              value={formData.evidenceSummaryColumn || ""}
              onChange={(e) =>
                handleChange(
                  "evidenceSummaryColumn",
                  e.target.value.toUpperCase() || null,
                )
              }
              placeholder="D"
              disabled={readOnly}
              className="uppercase w-20"
            />
            <p className="text-sm text-muted-foreground">
              Excel column letter for the evidence summary output
            </p>
          </div>
        )}

        {evidenceSummaryMode === "concat" && (
          <p className="ml-6 text-sm text-muted-foreground">
            The evidence summary will be appended to the outcome description
            column.
          </p>
        )}
      </div>

      <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm">
        <strong>Preview:</strong> {formData.sheetName} - Row {formData.startRow}
        + (expands vertically)
        <div className="mt-2 text-xs">
          Columns: {formData.attributeNameColumn} (Name),{" "}
          {formData.attributeOutcomeColumn} (Outcome)
          {formData.attributeOutcomeDescriptionColumn &&
            `, ${formData.attributeOutcomeDescriptionColumn} (Description)`}
          {formData.evidenceSummaryColumn &&
            `, ${formData.evidenceSummaryColumn} (Evidence Summary)`}
        </div>
        {formData.concatEvidenceWithDescription && (
          <div className="mt-1 text-xs text-muted-foreground">
            Evidence summary will be appended to description column
          </div>
        )}
      </div>
    </div>
  );
}
